import { DownOutlined, HolderOutlined } from '@ant-design/icons';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { Col, Input, Row, Select, Space } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import { memo, ReactNode } from 'react';
import { ControllerProps, useFormContext } from 'react-hook-form';
import styled, { css } from 'styled-components';

import { useProgramStepContext } from 'src/features/programsV2/contexts/ProgramStepContext';
import { ContentStepForm } from 'src/features/programsV2/schemas/contentStepSchema';
import { GenderOptions } from 'src/features/programsV2/types/program';
import { allowedFilesOptions } from 'src/features/programsV2/utils/formatAllowedFileTypes';

import { ProgramFormItem } from '../../ProgramFormItem';
import { DragButton } from '../../ui/DragButton';
import { ProgramSection } from '../../ui/ProgramSection';
import { TransparentButton } from '../../ui/TransparentButton';
import { ContentTypeFields } from './ContentTypeFields';
import { ThumbPreview } from './ThumbPreview';

interface ContentCardProps {
  moduleIndex: number;
  contentIndex: number;
  isOpen?: boolean;
  addonAfter?: ReactNode;
  onToggleDropdown?: () => void;
  isDragging: boolean | undefined;
  listeners: SyntheticListenerMap | undefined;
}

export const ContentCard = memo<ContentCardProps>(
  ({
    moduleIndex,
    contentIndex,
    addonAfter,
    isDragging,
    listeners,
    onToggleDropdown,
    isOpen,
  }) => {
    const { control, setValue } = useFormContext<ContentStepForm>();

    const resetContentType = () => {
      setValue(`modules.${moduleIndex}.contents.${contentIndex}.file`, {
        fileName: '',
        fileUrl: '',
      });
    };

    const { getStepDefaultValues, programApiInitialValues } =
      useProgramStepContext();

    const selectedGender =
      getStepDefaultValues('configurationStep')?.gender ??
      programApiInitialValues?.configurationStep?.gender;

    const thumbNames: Record<string, ControllerProps<ContentStepForm>['name']> =
      {
        neutral: `modules.${moduleIndex}.contents.${contentIndex}.neutralThumb`,
        female: `modules.${moduleIndex}.contents.${contentIndex}.femaleThumb`,
        male: `modules.${moduleIndex}.contents.${contentIndex}.maleThumb`,
      };

    return (
      <ProgramSection
        style={{
          margin: '0 0 16px 0',
          border: isDragging ? '1px dashed #187DDC' : undefined,
          borderRadius: '8px',
        }}
      >
        <Container>
          <DragButton {...listeners} isDragging={isDragging}>
            <HolderOutlined />
          </DragButton>

          <Space direction='vertical' size={isOpen ? 'middle' : 'small'}>
            <ThumbPreview
              genderType='neutral'
              genderThumbName={thumbNames.neutral}
              disabled={
                !selectedGender?.includes(GenderOptions.Neutral) || false
              }
            />

            {isOpen && (
              <>
                <ThumbPreview
                  genderType='woman'
                  genderThumbName={thumbNames.female}
                  disabled={
                    !selectedGender?.includes(GenderOptions.Female) || false
                  }
                />
                <ThumbPreview
                  genderType='man'
                  genderThumbName={thumbNames.male}
                  disabled={
                    !selectedGender?.includes(GenderOptions.Male) || false
                  }
                />
              </>
            )}
          </Space>

          <Space
            direction='vertical'
            size={'middle'}
            style={{
              width: '100%',
              marginLeft: '16px',
              marginTop: '6px',
              alignSelf: 'start',
            }}
          >
            <Row style={{ width: '100%' }} gutter={16}>
              <Col span={6}>
                <ProgramFormItem
                  control={control}
                  name={`modules.${moduleIndex}.contents[${contentIndex}].title`}
                  label='Título'
                  renderItem={({ field }) => (
                    <Input
                      onChange={field.onChange}
                      value={field.value ? field.value : undefined}
                      autoFocus={false}
                      placeholder='Titulo do novo conteúdo'
                    />
                  )}
                />
              </Col>
              <Col span={3}>
                <ProgramFormItem
                  control={control}
                  name={`modules.${moduleIndex}.contents.${contentIndex}.type`}
                  label='Tipo'
                  renderItem={({ field }) => (
                    <Select
                      options={allowedFilesOptions}
                      onChange={value => {
                        resetContentType();
                        field.onChange(value);
                      }}
                      value={field.value ? field.value : undefined}
                      placeholder='Selecionar'
                      autoFocus={false}
                    />
                  )}
                />
              </Col>
              <ContentTypeFields
                moduleIndex={moduleIndex}
                contentIndex={contentIndex}
                isOpen={isOpen || false}
                selectedGender={selectedGender?.[0]}
                thumbNames={thumbNames}
              />
            </Row>

            {isOpen && (
              <>
                {/* <Row style={{ width: '100%' }} gutter={16}>
                  <Col span={24}>
                    <ProgramFormItem
                      control={control}
                      name={`modules.${moduleIndex}.contents[${contentIndex}].tags`}
                      label='Tags (Opcional)'
                      renderItem={({ field, fieldState }) => (
                        <Select
                          mode='tags'
                          onChange={field.onChange}
                          value={field.value ? field.value : undefined}
                          placeholder='Selecione Tags..'
                          status={!!fieldState.error ? 'error' : undefined}
                        />
                      )}
                    />
                  </Col>
                </Row> */}
                <Row style={{ width: '100%' }} gutter={16}>
                  <Col span={24}>
                    <ProgramFormItem
                      control={undefined}
                      name={`modules.${moduleIndex}.contents[${contentIndex}].description`}
                      label='Descrição (Opcional)'
                      renderItem={({ field }) => (
                        <TextArea
                          onChange={field.onChange}
                          value={field.value ? field.value : undefined}
                          placeholder='Descrição do novo conteúdo'
                        />
                      )}
                    />
                  </Col>
                </Row>
              </>
            )}
          </Space>

          {addonAfter}

          <TransparentButton
            icon={<Arrow isOpen={isOpen} />}
            onClick={onToggleDropdown}
            style={{
              width: '22px',
              height: '22px',
              marginLeft: '8px',
            }}
          />
        </Container>
      </ProgramSection>
    );
  }
);

export const Container = styled.div`
  padding: 16px;
  display: flex;
  align-items: center;
  flex: 1;
`;

const Arrow = styled(DownOutlined)<{
  isOpen: boolean | undefined;
}>`
  transition: transform 0.3s ease;
  ${({ isOpen }) =>
    isOpen &&
    css`
      transform: rotate(180deg);
    `}
`;
